import { useCallback } from 'react';
import { useRouter, useRouterState } from '@tanstack/react-router';

/**
 * The Settings dialog's URL state: `?settings=<page>` on whatever route is
 * showing, so the dialog opens over the current page and any settings page
 * is a shareable link (`/issues/foo?settings=reading`). No route declares
 * it -- TanStack Router keeps unknown search params in the URL -- which is
 * what lets one dialog, mounted at the root, work on every template's
 * routes without touching their routers.
 *
 * Lives in `shared/` rather than with the dialog (`widgets/settings-dialog`)
 * because the menus that open it are in `shared/ui` too.
 */
export const SETTINGS_SEARCH_PARAM = 'settings';

/**
 * History-state marker for an entry this module pushed. Closing pops it
 * (so Back and the close button agree) only when it's there; a dialog
 * opened from a pasted link has nothing of ours to pop, so closing just
 * rewrites the URL instead of leaving the site.
 */
const OPENED_IN_APP = '__settingsOpenedInApp';

/** `currentHref` with the param set to `page`, or removed for `null`. */
function hrefWith(currentHref: string, page: string | null): string {
	const url = new URL(currentHref, 'http://settings.invalid');
	if (page) {
		url.searchParams.set(SETTINGS_SEARCH_PARAM, page);
	} else {
		url.searchParams.delete(SETTINGS_SEARCH_PARAM);
	}
	return `${url.pathname}${url.search}${url.hash}`;
}

/** The open settings page's id, or `null` while the dialog is closed. */
export function useSettingsPage(): string | null {
	return useRouterState({
		select: (state) => {
			const value = (state.location.search as Record<string, unknown>)[
				SETTINGS_SEARCH_PARAM
			];
			return value == null || value === '' ? null : String(value);
		},
	});
}

/**
 * `openSettings(page?)` pushes a history entry, so Back closes the dialog.
 * `selectPage` replaces it, so Back doesn't walk through every page
 * visited. `closeSettings` pops our entry, or drops the param when the
 * dialog was opened from a link.
 */
export function useSettingsNavigation() {
	const { history } = useRouter();

	const openSettings = useCallback(
		(page: string = DEFAULT_SETTINGS_PAGE) => {
			history.push(hrefWith(history.location.href, page), {
				...history.location.state,
				[OPENED_IN_APP]: true,
			} as typeof history.location.state);
		},
		[history],
	);

	const selectPage = useCallback(
		(page: string) => {
			history.replace(hrefWith(history.location.href, page), history.location.state);
		},
		[history],
	);

	const closeSettings = useCallback(() => {
		const state = history.location.state as Record<string, unknown>;
		if (state?.[OPENED_IN_APP]) {
			history.back();
			return;
		}
		history.replace(hrefWith(history.location.href, null), history.location.state);
	}, [history]);

	return { openSettings, selectPage, closeSettings };
}

/** Where the menus' Settings entry opens. */
export const DEFAULT_SETTINGS_PAGE = 'general';
